import { accumulate } from '../../../helpers';
import { AlkLogger } from '@alk/core';
import { concat, Observable, Subject, Subscription } from 'rxjs';
import { finalize, map, shareReplay, skip, takeUntil } from 'rxjs/operators';
import { inject, injectable } from 'inversify';
import { IPersistenceAdapterService, ISettingsPartialRecord, ISettingsResult } from './persistence-adapter.interface';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../../session-lifecycle';
import { ISettings } from '../../../interfaces';
import { SerializedRecord } from '../../../interfaces/functions.interface';

@injectable()
export abstract class PersistenceAdapterAbstractService implements IPersistenceAdapterService {

    @inject(SESSION_LIFECYCLE_SERVICE) protected sessionLifeCycle: ISessionLifecycle;

    protected abstract serviceName;

    private changesObs = new Subject<ISettingsPartialRecord>();
    private saveSubscription: Subscription;

    protected abstract getInitialSettingsObsInternal(): Observable<ISettingsPartialRecord>;
    protected abstract saveInternal(change: Partial<SerializedRecord<ISettings>>): Promise<void>;

    private resultObs: Observable<ISettingsPartialRecord>;
    private readonly getResultObs = (): Observable<ISettingsPartialRecord> => {

        if (!Boolean(this.resultObs)) {
            this.resultObs = concat(
                this.getInitialSettingsObsInternal(),
                this.changesObs,
            ).pipe(
                accumulate({} as ISettingsPartialRecord),
                shareReplay(1),
                takeUntil(this.sessionLifeCycle.destroyObservable()),
                finalize(() => {
                    this.clear();
                }),
            );

            this.saveSubscription = this.resultObs.pipe(skip(1)).subscribe(async (change) => {
                await this.saveInternal(change);
            });
        }

        return this.resultObs;
    };

    private readonly clear = (): void => {
        this.saveSubscription?.unsubscribe();
        this.saveSubscription = null;
        this.resultObs = null;
        this.changesObs = new Subject<ISettingsPartialRecord>();
    };

    settingsObs = <K extends ReadonlyArray<keyof ISettings>>(keys: K): Observable<ISettingsResult<K>> =>
        this.getResultObs().pipe(
            map(settings =>
                keys.reduce(
                    (acc, cur) => ({ ...acc, ...{ [ cur ]: settings[ cur ] } })
                    , {} as ISettingsResult<K>,
                ),
            ),
        );

    save = (changedSettings: Partial<SerializedRecord<ISettings>>): void => {

        AlkLogger.info(`[${ this.serviceName }#save]`, changedSettings);

        this.getResultObs();

        this.changesObs.next(changedSettings);
    };
}
